import { uniqBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useRefState } from "@inception/ui";
import { useCaseApiService, UseCaseUnderlyingInfo } from "../../services/api";
import { BizEntityType, ResultEntity } from "../../services/api/explore";
import { logger } from "../logging/Logger";
import { useTenantConfig } from "./tenant-config";
import { useVerticalConfig, VerticalUseCaseSummary, VerticalUseCaseUnderlyingInfo } from "./vertical-config";

type Props = {
  includeEventTypeIds: boolean;
  includeEntityTypes: boolean;
  includeEventEntityMap: boolean;
  includeEntityTypesAsEvents: boolean;
};

export const useFetchUseCaseUnderlyingInfo = (props: Props) => {
  const {
    includeEventEntityMap = true,
    includeEntityTypesAsEvents = false,
    includeEventTypeIds = true,
    includeEntityTypes = true
  } = props;

  const fetchEventTypesOnly = includeEventTypeIds && (!includeEntityTypes || !includeEventEntityMap);

  // Update data in store only when the default behaviour matches the caller behaviour
  const shouldUseReduxStore =
    includeEventEntityMap && includeEntityTypes && includeEventTypeIds && !includeEntityTypesAsEvents;

  const { tenantConfigState } = useTenantConfig();
  const { useNewNavigation, demoTenant } = tenantConfigState || {};

  const { useCaseApi, useCaseSchemaApi, verticalConfig } = useVerticalConfig();
  const { useCaseToUnderlyingInfo } = verticalConfig;

  const underlyingInfoRef = useRefState(useCaseToUnderlyingInfo);

  const { getSelectedUseCases } = useCaseApi;
  const { updateUnderlyingInfo } = useCaseSchemaApi;

  const { useCaseIds, useCasesMap } = useMemo(() => {
    const useCases = getSelectedUseCases();
    return {
      useCaseIds: useCases.map(useCase => useCase.id),
      useCasesMap: useCases.reduce(
        (acc, useCase) => ({
          ...acc,
          [useCase.id]: useCase
        }),
        {} as Record<string, VerticalUseCaseSummary>
      )
    };
  }, [getSelectedUseCases]);

  const [state, setState] = useState<State>({
    isFetching: useNewNavigation,
    isSuccess: false,
    bizEntityTypes: [],
    entityTypeToEventTypeMap: {},
    eventIdToEntityTypeMap: {},
    eventTypes: []
  });

  const fetchUnderlyingInfo = useCallback(async () => {
    setState({
      isSuccess: false,
      isFetching: true,
      bizEntityTypes: [],
      entityTypeToEventTypeMap: {},
      eventIdToEntityTypeMap: {},
      eventTypes: []
    });

    const useCaseToUnderlyingInfo = underlyingInfoRef.current;
    const promises = useCaseIds.map(useCaseId => {
      const existingInfo = useCaseToUnderlyingInfo[useCaseId];
      if (
        shouldUseReduxStore &&
        existingInfo &&
        (!fetchEventTypesOnly ? existingInfo.bizEntityTypes?.length > 0 : true)
      ) {
        return Promise.resolve({
          data: existingInfo,
          error: false,
          message: ""
        });
      }

      if (demoTenant) {
        const { dataSettings, involvedEventTypes } = useCasesMap[useCaseId];
        const { eventTypeSettings, lookupData: eventTypesLookup } = dataSettings?.eventTypeSettingsList || {};
        let eventTypes: ResultEntity[];

        if (eventTypeSettings?.length > 0) {
          eventTypes = eventTypeSettings.map(({ eventTypeId }) => ({
            entityId: eventTypeId,
            name: eventTypesLookup?.[eventTypeId] || eventTypeId
          }));
        } else {
          eventTypes = (involvedEventTypes || []).map(eventType => ({
            entityId: eventType,
            name: eventType
          }));
        }

        const data: VerticalUseCaseUnderlyingInfo = {
          bizEntityTypes: [],
          entityTypeToEventIds: {},
          eventIdToEntityTypes: {},
          eventTypes
        };

        return Promise.resolve({
          data,
          error: false,
          message: ""
        });
      } else {
        const fetchPromise = useCaseApiService.getUnderlyingInfoForUseCase(
          useCaseId,
          includeEventTypeIds,
          includeEntityTypes,
          includeEventEntityMap,
          includeEntityTypesAsEvents
        );
        return fetchPromise.then(response => {
          const { data, ...restResponse } = response;

          return {
            data: getVerticalUseCaseUnderlyingInfo(data),
            ...restResponse
          };
        });
      }
    });

    const underlyingInfoMap: Record<string, VerticalUseCaseUnderlyingInfo> = {};

    const results = await Promise.allSettled(promises);
    results.forEach((result, idx) => {
      if (result.status === "fulfilled") {
        const { data, error, message } = result.value;

        if (error) {
          logger.error("useFetchUseCaseUnderlyingInfo", "Error while fetching Copilot underlying info", message);
        } else {
          const useCaseId = useCaseIds[idx];
          underlyingInfoMap[useCaseId] = data;
        }
      } else {
        logger.error("useFetchUseCaseUnderlyingInfo", "Error while fetching Copilot underlying info", result.reason);
      }
    });

    if (shouldUseReduxStore) {
      updateUnderlyingInfo(underlyingInfoMap);
    }

    const nextState = getStateFromUnderlyingInfoMap(underlyingInfoMap);
    setState(nextState);
  }, [
    demoTenant,
    fetchEventTypesOnly,
    includeEntityTypes,
    includeEntityTypesAsEvents,
    includeEventEntityMap,
    includeEventTypeIds,
    shouldUseReduxStore,
    underlyingInfoRef,
    updateUnderlyingInfo,
    useCaseIds,
    useCasesMap
  ]);

  return {
    ...state,
    fetchUnderlyingInfo
  };
};

type State = {
  isSuccess: boolean;
  isFetching: boolean;
  bizEntityTypes: BizEntityType[];
  entityTypeToEventTypeMap: Record<string, ResultEntity[]>;
  eventTypes: ResultEntity[];
  eventIdToEntityTypeMap: Record<string, BizEntityType[]>;
};

const getVerticalUseCaseUnderlyingInfo = (underlyingInfo: UseCaseUnderlyingInfo): VerticalUseCaseUnderlyingInfo => {
  if (!underlyingInfo) {
    return {
      bizEntityTypes: [],
      eventTypes: [],
      entityTypeToEventIds: {},
      eventIdToEntityTypes: {}
    };
  }

  const { entityTypeIds, entityTypeToEventId, eventIdToEntityType, eventIds } = underlyingInfo;

  const vUnderlyingInfo: VerticalUseCaseUnderlyingInfo = {
    bizEntityTypes: entityTypeIds?.resultEntityTypes || [],
    entityTypeToEventIds: {},
    eventIdToEntityTypes: {},
    eventTypes: eventIds?.resultEntities || []
  };

  Object.keys(entityTypeToEventId || {}).forEach(entityTypeId => {
    const eventTypeIds = underlyingInfo?.entityTypeToEventId[entityTypeId].resultEntities.map(re => re.entityId);
    vUnderlyingInfo.entityTypeToEventIds[entityTypeId] = eventTypeIds;
  });

  Object.keys(eventIdToEntityType || {}).forEach(eventId => {
    const entityTypeIds = underlyingInfo?.eventIdToEntityType[eventId].resultEntityTypes.map(re => re.entityTypeId);
    vUnderlyingInfo.eventIdToEntityTypes[eventId] = entityTypeIds;
  });

  return vUnderlyingInfo;
};

const getStateFromUnderlyingInfoMap = (underlyingInfoMap: Record<string, VerticalUseCaseUnderlyingInfo>): State => {
  const state: State = {
    isSuccess: true,
    isFetching: false,
    bizEntityTypes: [],
    entityTypeToEventTypeMap: {},
    eventTypes: [],
    eventIdToEntityTypeMap: {}
  };

  Object.keys(underlyingInfoMap).forEach(useCaseId => {
    const { bizEntityTypes, eventTypes, entityTypeToEventIds, eventIdToEntityTypes } = underlyingInfoMap[useCaseId];

    state.bizEntityTypes = [...state.bizEntityTypes, ...bizEntityTypes];
    state.eventTypes = [...state.eventTypes, ...eventTypes];

    Object.keys(entityTypeToEventIds).forEach(entityTypeId => {
      const eventTypeIds = entityTypeToEventIds[entityTypeId];
      eventTypeIds.forEach(eventTypeId => {
        const eventType = eventTypes.find(et => et.entityId === eventTypeId);
        if (eventType) {
          state.entityTypeToEventTypeMap[entityTypeId] = [
            ...(state.entityTypeToEventTypeMap[entityTypeId] || []),
            eventType
          ];
        }
      });
    });

    Object.keys(eventIdToEntityTypes).forEach(eventTypeId => {
      const entityTypeIds = eventIdToEntityTypes[eventTypeId];
      entityTypeIds.forEach(entityTypeId => {
        const bizEntityType = bizEntityTypes.find(et => et.entityTypeId === entityTypeId);
        if (bizEntityType) {
          state.eventIdToEntityTypeMap[eventTypeId] = [
            ...(state.eventIdToEntityTypeMap[eventTypeId] || []),
            bizEntityType
          ];
        }
      });
    });
  });

  state.bizEntityTypes = uniqBy(state.bizEntityTypes, be => be.entityTypeId);
  state.eventTypes = uniqBy(state.eventTypes, et => et.entityId);

  Object.keys(state.entityTypeToEventTypeMap).forEach(entityTypeId => {
    state.entityTypeToEventTypeMap[entityTypeId] = uniqBy(
      state.entityTypeToEventTypeMap[entityTypeId],
      et => et.entityId
    );
  });

  Object.keys(state.eventIdToEntityTypeMap).forEach(eventTypeId => {
    state.eventIdToEntityTypeMap[eventTypeId] = uniqBy(
      state.eventIdToEntityTypeMap[eventTypeId],
      et => et.entityTypeId
    );
  });

  return state;
};
